import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Crown, Send, Sparkles, Facebook, Mail, Users, Lightbulb, Check, X, Copy, RefreshCw, Trash2, Play, Loader2, FileText, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AGENT_META = {
  social: { label: 'Social Agent', icon: Facebook, color: 'bg-blue-100 text-blue-700' },
  newsletter: { label: 'Newsletter Agent', icon: Mail, color: 'bg-emerald-100 text-emerald-700' },
  reengagement: { label: 'Re-engagement Agent', icon: RefreshCw, color: 'bg-amber-100 text-amber-700' },
  outreach: { label: 'Outreach Agent', icon: Users, color: 'bg-purple-100 text-purple-700' },
  brain: { label: 'Marketing Brain', icon: Lightbulb, color: 'bg-pink-100 text-pink-700' },
  support: { label: 'Support Agent', icon: Bot, color: 'bg-teal-100 text-teal-700' },
  investor: { label: 'Investor Scout', icon: TrendingUp, color: 'bg-indigo-100 text-indigo-700' },
};

const api = async (path, opts = {}) => {
  const res = await fetch(`${API}/api/admin/ai-team${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
};

const TaskCard = ({ task, onRefresh }) => {
  const meta = AGENT_META[task.agent] || AGENT_META.brain;
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(task.content || {});
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    setBusy(true);
    try {
      if (editing) {
        await api(`/tasks/${task.id}`, { method: 'PUT', body: JSON.stringify({ content }) });
        setEditing(false);
      }
      if (action) {
        const r = await api(`/tasks/${task.id}/${action}`, { method: 'POST' });
        toast.success(action === 'approve' ? `Done — ${r.status}${r.recipients ? ` to ${r.recipients} people` : ''}` : 'Rejected');
      } else {
        toast.success('Draft updated');
      }
      onRefresh();
    } catch (e) {
      toast.error(e.message);
      onRefresh();
    } finally {
      setBusy(false);
    }
  };

  const copyPost = () => {
    const text = `${content.caption || ''}\n\n${content.hashtags || ''}\n${content.link || ''}`.trim();
    navigator.clipboard.writeText(text);
    toast.success('Post copied — paste it on your Facebook page');
  };

  const isDm = task.agent === 'outreach' && !!content.dm_script;
  const bodyField = task.agent === 'social' ? 'caption' : task.agent === 'brain' ? 'detail' : isDm ? 'dm_script' : 'body_html';

  const copyDm = () => {
    navigator.clipboard.writeText(content.dm_script || '');
    toast.success(`DM copied — paste it to ${content.handle || 'them'} on ${content.platform || 'Instagram'}`);
  };

  return (
    <div className="bg-white rounded-xl border p-4" data-testid={`ai-task-${task.id}`}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 ${meta.color}`}>
          <meta.icon className="w-3 h-3" /> {meta.label} {task.auto && <span className="opacity-60">· auto</span>}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${task.status === 'draft' ? 'bg-gray-100 text-gray-600' : task.status === 'failed' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
          {task.status}
        </span>
      </div>
      <p className="font-medium text-sm text-gray-900 mb-1">{task.title}</p>
      {task.needs_founder && (
        <span className="inline-block text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full mb-1" data-testid={`ai-task-financial-${task.id}`}>
          {task.agent === 'investor' ? '🤝 Investor pitch — sends only with your approval' : '💰 Financial — needs your call'}
        </span>
      )}
      {content.subject && !editing && <p className="text-xs text-gray-500 mb-1">Subject: {content.subject}</p>}
      {task.error && <p className="text-xs text-red-500 mb-1">{task.error}</p>}
      {editing ? (
        <div className="space-y-2 my-2">
          {'subject' in content && (
            <Input value={content.subject || ''} onChange={e => setContent({ ...content, subject: e.target.value })} placeholder="Subject" data-testid="ai-task-edit-subject" />
          )}
          <Textarea rows={5} value={content[bodyField] || ''} onChange={e => setContent({ ...content, [bodyField]: e.target.value })} data-testid="ai-task-edit-body" />
          {task.agent === 'social' && (
            <Input value={content.hashtags || ''} onChange={e => setContent({ ...content, hashtags: e.target.value })} placeholder="#hashtags" />
          )}
        </div>
      ) : (
        <div className="text-xs text-gray-600 my-2 max-h-28 overflow-y-auto whitespace-pre-wrap"
          dangerouslySetInnerHTML={{ __html: (content[bodyField] || content.why || '').replace(/<script/gi, '') }} />
      )}
      {task.agent === 'social' && content.image_url && !editing && (
        <img src={content.image_url} alt="" className="w-16 h-16 object-cover rounded-lg mb-2" />
      )}
      {task.status === 'draft' && (
        <div className="flex gap-2 flex-wrap mt-2">
          {isDm ? (
            <Button size="sm" onClick={copyDm} className="bg-minted-600 hover:bg-minted-700" data-testid={`ai-task-copydm-${task.id}`}>
              <Copy className="w-3 h-3 mr-1" /> Copy DM
            </Button>
          ) : (
            <Button size="sm" onClick={() => act('approve')} disabled={busy} className="bg-minted-600 hover:bg-minted-700" data-testid={`ai-task-approve-${task.id}`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
              {task.agent === 'social' ? 'Approve & Post' : task.agent === 'brain' ? 'Action It' : 'Approve & Send'}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => editing ? act(null) : setEditing(true)} disabled={busy} data-testid={`ai-task-edit-${task.id}`}>
            {editing ? 'Save' : 'Edit'}
          </Button>
          {task.agent === 'social' && (
            <Button size="sm" variant="outline" onClick={copyPost} data-testid={`ai-task-copy-${task.id}`}>
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => act('reject')} disabled={busy} className="text-red-500" data-testid={`ai-task-reject-${task.id}`}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

export const AITeamTab = () => {
  const [overview, setOverview] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('draft');
  const [generating, setGenerating] = useState('');
  const [runningCeo, setRunningCeo] = useState(false);
  const [ceoQuestion, setCeoQuestion] = useState('');
  const [askingCeo, setAskingCeo] = useState(false);
  const [investorPack, setInvestorPack] = useState(null);
  const [packBusy, setPackBusy] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', handle: '', type: 'influencer', notes: '' });
  const [fbForm, setFbForm] = useState({ fb_page_id: '', fb_page_token: '' });
  const [showFbSetup, setShowFbSetup] = useState(false);
  const [prospects, setProspects] = useState(null);
  const [invProspects, setInvProspects] = useState(null);
  const [newInvestor, setNewInvestor] = useState({ name: '', email: '', notes: '' });

  const load = useCallback(async () => {
    try {
      const [ov, tk, ct, rp] = await Promise.all([
        api('/overview'), api(`/tasks?status=${statusFilter}`), api('/contacts'), api('/reports?limit=5'),
      ]);
      setOverview(ov); setTasks(tk); setContacts(ct); setReports(rp);
    } catch (e) { toast.error(e.message); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api('/investor-pack/latest').then(setInvestorPack).catch(() => {}); }, []);

  const buildInvestorPack = async () => {
    setPackBusy(true);
    try {
      await api('/investor-pack', { method: 'POST' });
      toast.success('The CEO is preparing your investor pack — about a minute');
      const poll = setInterval(async () => {
        try {
          const p = await api('/investor-pack/latest');
          if (p.status === 'ready') {
            clearInterval(poll);
            setInvestorPack(p);
            setPackBusy(false);
            toast.success('Investor pack ready!');
          } else if (p.status === 'failed') {
            clearInterval(poll);
            setPackBusy(false);
            toast.error('Pack generation failed — try again');
          }
        } catch { /* keep polling */ }
      }, 6000);
      setTimeout(() => { clearInterval(poll); setPackBusy(false); }, 180000);
    } catch (e) { toast.error(e.message); setPackBusy(false); }
  };

  const viewInvestorPack = () => {
    window.open(`${API}/api/admin/ai-team/investor-pack/${investorPack.id}/html`, '_blank');
  };

  const generate = async (agent, body = {}) => {
    setGenerating(agent);
    try {
      await api(`/generate/${agent}`, { method: 'POST', body: JSON.stringify(body) });
      toast.success(`${AGENT_META[agent].label} drafted something — check the queue`);
      setStatusFilter('draft');
      load();
    } catch (e) { toast.error(e.message); } finally { setGenerating(''); }
  };

  const runCeo = async () => {
    setRunningCeo(true);
    try {
      const r = await api('/ceo/run', { method: 'POST' });
      toast.success(r.status === 'already_running' ? 'CEO is already working on it' : 'AI CEO is on it — the report will appear here in about a minute');
      const before = overview?.latest_report?.created_at;
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const ov = await api('/overview');
          if (ov.latest_report?.created_at !== before && ov.latest_report) {
            clearInterval(poll);
            setOverview(ov);
            setRunningCeo(false);
            toast.success('CEO report ready (also emailed to admin@)');
            load();
          } else if (!ov.ceo_running && tries > 2) {
            clearInterval(poll);
            setRunningCeo(false);
            load();
          } else if (tries > 20) {
            clearInterval(poll);
            setRunningCeo(false);
          }
        } catch { /* keep polling */ }
      }, 8000);
    } catch (e) {
      toast.error(e.message);
      setRunningCeo(false);
    }
  };

  const askCeo = async () => {
    if (!ceoQuestion.trim()) return toast.error('Type your request first');
    setAskingCeo(true);
    try {
      await api('/ceo/ask', { method: 'POST', body: JSON.stringify({ question: ceoQuestion }) });
      toast.success("Sent — the CEO's reply will land in your email in a minute or two");
      setCeoQuestion('');
    } catch (e) { toast.error(e.message); } finally { setAskingCeo(false); }
  };

  const toggleSetting = async (key) => {
    const val = !overview.settings[key];
    await api('/settings', { method: 'PUT', body: JSON.stringify({ [key]: val }) });
    setOverview({ ...overview, settings: { ...overview.settings, [key]: val } });
    toast.success(`${key.replace(/_/g, ' ')} ${val ? 'ON' : 'OFF'}`);
  };

  const saveFb = async () => {
    try {
      await api('/settings', { method: 'PUT', body: JSON.stringify(fbForm) });
      const test = await api('/facebook/test', { method: 'POST' });
      if (test.connected) toast.success(`Connected to ${test.page_name}! Approved posts will now auto-publish`);
      else toast.error(`Saved but connection failed: ${test.error}`);
      setShowFbSetup(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const addContact = async () => {
    if (!newContact.email && !newContact.handle) return toast.error('Add an email or a social handle');
    try {
      await api('/contacts', { method: 'POST', body: JSON.stringify(newContact) });
      setNewContact({ name: '', email: '', handle: '', type: 'influencer', notes: '' });
      toast.success('Contact added');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const findProspects = async () => {
    setGenerating('prospects');
    setProspects(null);
    try {
      const r = await api('/prospect-ideas', { method: 'POST', body: JSON.stringify({}) });
      setProspects(r.prospects || []);
      if (!r.prospects?.length) toast.error('The AI returned nothing — tap again to retry');
    } catch (e) {
      setProspects([]);
      toast.error(e.message);
    } finally { setGenerating(''); }
  };

  const scoutInvestors = async () => {
    setGenerating('invScout');
    setInvProspects(null);
    try {
      const r = await api('/investor/prospects', { method: 'POST', body: JSON.stringify({}) });
      setInvProspects(r.prospects || []);
      if (!r.prospects?.length) toast.error('The Scout returned nothing — tap again to retry');
    } catch (e) {
      setInvProspects([]);
      toast.error(e.message);
    } finally { setGenerating(''); }
  };

  const addInvestor = async () => {
    if (!newInvestor.name && !newInvestor.email) return toast.error('Add at least a name or email');
    try {
      await api('/contacts', { method: 'POST', body: JSON.stringify({ ...newInvestor, email: newInvestor.email, handle: newInvestor.email ? '' : (newInvestor.name || 'investor'), type: 'investor' }) });
      setNewInvestor({ name: '', email: '', notes: '' });
      toast.success('Investor added');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const draftInvestorPitch = async (contactId) => {
    setGenerating(`invPitch-${contactId}`);
    try {
      await api('/investor/pitch', { method: 'POST', body: JSON.stringify({ contact_id: contactId }) });
      toast.success('Pitch drafted — review and approve it in the work queue (it never sends without you)');
      setStatusFilter('draft');
      load();
    } catch (e) { toast.error(e.message); } finally { setGenerating(''); }
  };

  if (!overview) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-minted-600" /></div>;

  const latestReport = overview.latest_report;

  return (
    <div className="space-y-6" data-testid="ai-team-tab">
      {/* CEO Card */}
      <div className="bg-[#1a3a2a] text-white rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><Crown className="w-5 h-5 text-amber-300" /></div>
            <div>
              <h3 className="font-serif text-lg" style={{ fontFamily: 'Georgia, serif' }}>AI CEO</h3>
              <p className="text-xs text-white/60">Runs daily at 9am · reports emailed to admin@</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={runCeo} disabled={runningCeo || overview.ceo_running} className="bg-white text-[#1a3a2a] hover:bg-white/90" data-testid="ai-ceo-run-btn">
              {(runningCeo || overview.ceo_running) ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Working…</> : <><Play className="w-4 h-4 mr-1" /> Run now</>}
            </Button>
            {investorPack?.status === 'ready' ? (
              <Button size="sm" onClick={viewInvestorPack} className="bg-amber-300 text-[#1a3a2a] hover:bg-amber-200" data-testid="ai-investor-pack-view-btn">
                <FileText className="w-4 h-4 mr-1" /> View Investor Pack
              </Button>
            ) : null}
            <Button size="sm" variant="outline" onClick={buildInvestorPack} disabled={packBusy} className="border-white/30 bg-transparent text-white hover:bg-white/10" data-testid="ai-investor-pack-btn">
              {packBusy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Preparing…</> : <><FileText className="w-4 h-4 mr-1" /> {investorPack?.status === 'ready' ? 'Refresh Pack' : 'Investor Pack'}</>}
            </Button>
          </div>
        </div>
        <div className="mt-4 bg-white/5 rounded-xl p-3">
          <p className="text-xs text-white/60 mb-2 flex items-center gap-1"><Mail className="w-3 h-3" /> Ask the CEO anything — the reply lands in your email</p>
          <div className="flex gap-2 items-start flex-wrap">
            <Textarea
              rows={2}
              value={ceoQuestion}
              onChange={e => setCeoQuestion(e.target.value)}
              placeholder='e.g. "How are sales tracking?" or "Draft a newsletter about our new spring listings"'
              className="flex-1 min-w-[220px] bg-white/10 border-white/10 text-white placeholder:text-white/30 text-sm"
              data-testid="ai-ceo-question-input"
            />
            <Button size="sm" onClick={askCeo} disabled={askingCeo} className="bg-amber-300 text-[#1a3a2a] hover:bg-amber-200" data-testid="ai-ceo-ask-btn">
              {askingCeo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Ask
            </Button>
          </div>
          <p className="text-[10px] text-white/40 mt-2">If you ask for content (posts, newsletters, campaigns) the CEO also queues drafts here for your approval.</p>
        </div>
        {latestReport && (
          <div className="mt-4 bg-white/5 rounded-xl p-4">
            <p className="font-medium text-sm mb-1">{latestReport.headline}</p>
            <div className="text-xs text-white/70 max-h-24 overflow-y-auto" dangerouslySetInnerHTML={{ __html: latestReport.report_html }} />
            {latestReport.priorities?.length > 0 && (
              <ul className="mt-2 space-y-1">{latestReport.priorities.map((p, i) => (
                <li key={i} className="text-xs text-amber-200 flex gap-1"><Sparkles className="w-3 h-3 shrink-0 mt-0.5" />{p}</li>))}
              </ul>
            )}
            <p className="text-[10px] text-white/40 mt-2">{new Date(latestReport.created_at).toLocaleString()}</p>
          </div>
        )}
      </div>

      {/* Autopilot + Facebook */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Bot className="w-4 h-4" /> Autopilot</h4>
          <label className="flex items-center justify-between py-2 text-sm cursor-pointer border-b mb-1">
            <span className="pr-2"><span className="font-medium">Full team autonomy</span><br />
              <span className="text-xs text-gray-400">Every draft auto-executes within minutes — only 💰 financial items wait for you</span></span>
            <button onClick={() => toggleSetting('full_autonomy')} data-testid="ai-toggle-full_autonomy"
              className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ${overview.settings.full_autonomy ? 'bg-minted-600' : 'bg-gray-200'}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${overview.settings.full_autonomy ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </label>
          {[
            ['newsletter_autopilot', 'Weekly newsletter (Mondays)'],
            ['reengagement_autopilot', 'Win-back emails (Thursdays)'],
            ['social_autogen', 'Draft Facebook posts daily (you approve)'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center justify-between py-2 text-sm cursor-pointer">
              <span>{label}</span>
              <button onClick={() => toggleSetting(key)} data-testid={`ai-toggle-${key}`}
                className={`w-10 h-5 rounded-full transition-colors relative ${overview.settings[key] ? 'bg-minted-600' : 'bg-gray-200'}`}>
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${overview.settings[key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </label>
          ))}
        </div>
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Facebook className="w-4 h-4 text-blue-600" /> Facebook Page</h4>
          {overview.facebook?.connected ? (
            <p className="text-sm text-emerald-700 flex items-center gap-1"><Check className="w-4 h-4" /> Connected to {overview.facebook.page_name} — approved posts auto-publish</p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-2">Not connected — approved posts use the Copy button until you paste your Page ID + token here.</p>
              {showFbSetup ? (
                <div className="space-y-2">
                  <Input placeholder="Facebook Page ID" value={fbForm.fb_page_id} onChange={e => setFbForm({ ...fbForm, fb_page_id: e.target.value })} data-testid="ai-fb-page-id" />
                  <Input placeholder="Page Access Token" type="password" value={fbForm.fb_page_token} onChange={e => setFbForm({ ...fbForm, fb_page_token: e.target.value })} data-testid="ai-fb-token" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveFb} data-testid="ai-fb-save">Save & test</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowFbSetup(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setShowFbSetup(true)} data-testid="ai-fb-connect-btn">Connect Facebook</Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Generate buttons */}
      <div className="bg-white rounded-xl border p-4">
        <h4 className="font-semibold text-sm mb-3">Ask an agent to draft something now</h4>
        <div className="flex gap-2 flex-wrap">
          {['social', 'newsletter', 'reengagement', 'brain'].map(agent => {
            const m = AGENT_META[agent];
            return (
              <Button key={agent} size="sm" variant="outline" onClick={() => generate(agent)} disabled={!!generating} data-testid={`ai-generate-${agent}`}>
                {generating === agent ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <m.icon className="w-3 h-3 mr-1" />}
                {m.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Task queue */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="font-semibold flex items-center gap-2"><Send className="w-4 h-4" /> Work queue
            <span className="text-xs bg-minted-100 text-minted-700 px-2 py-0.5 rounded-full">{overview.queue?.draft || 0} awaiting approval</span>
          </h4>
          <div className="flex gap-1">
            {['draft', 'sent', 'posted', 'rejected', 'failed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`ai-filter-${s}`}
                className={`text-xs px-3 py-1 rounded-full ${statusFilter === s ? 'bg-minted-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{s}</button>
            ))}
          </div>
        </div>
        {tasks.length === 0 ? (
          <p className="text-sm text-gray-400 bg-white rounded-xl border p-6 text-center">Nothing here — ask an agent to draft something, or run the CEO.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {tasks.map(t => <TaskCard key={t.id} task={t} onRefresh={load} />)}
          </div>
        )}
      </div>

      {/* Outreach contacts */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="font-semibold text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Outreach contacts</h4>
          <Button size="sm" variant="outline" onClick={findProspects} disabled={!!generating} data-testid="ai-prospect-ideas-btn">
            {generating === 'prospects' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Lightbulb className="w-3 h-3 mr-1" />} Who should we contact?
          </Button>
        </div>
        {prospects && prospects.length === 0 && (
          <p className="mb-3 text-xs text-red-500 bg-red-50 rounded-lg p-3" data-testid="ai-prospects-error">Couldn't fetch suggestions — tap "Who should we contact?" again to retry.</p>
        )}
        {prospects && prospects.length > 0 && (
          <div className="mb-3 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="ai-prospects-list">
            {prospects.map((p, i) => (
              <div key={i} className="text-xs">
                <span className="font-medium">{p.who}</span>
                {p.handle && <span className="text-minted-700 ml-1">{p.handle}</span>}
                {p.platform && <span className="text-gray-400 ml-1">on {p.platform}</span>}
                <div className="text-gray-600">{p.why} <span className="text-gray-400">({p.where_to_find})</span></div>
              </div>
            ))}
            <p className="text-[10px] text-gray-400">Verify the account exists, then add them below (handle is enough — no email needed) and the AI writes the pitch or DM.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-5 gap-2 mb-3">
          <Input placeholder="Name" value={newContact.name} onChange={e => setNewContact({ ...newContact, name: e.target.value })} data-testid="ai-contact-name" />
          <Input placeholder="Email (if known)" value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} data-testid="ai-contact-email" />
          <Input placeholder="@handle (if no email)" value={newContact.handle} onChange={e => setNewContact({ ...newContact, handle: e.target.value })} data-testid="ai-contact-handle" />
          <Input placeholder="Notes (e.g. fashion, 20k)" value={newContact.notes} onChange={e => setNewContact({ ...newContact, notes: e.target.value })} data-testid="ai-contact-notes" />
          <Button size="sm" onClick={addContact} data-testid="ai-contact-add">Add</Button>
        </div>
        <p className="text-[10px] text-gray-400 mb-1">No email? Just add their @handle — the AI writes a DM you copy-paste instead.</p>
        {contacts.filter(c => c.type !== 'investor').map(c => (
          <div key={c.id} className="flex items-center justify-between py-2 border-t text-sm gap-2 flex-wrap">
            <div>
              <span className="font-medium">{c.name || c.email || c.handle}</span>
              <span className="text-xs text-gray-400 ml-2">{c.email || c.handle}{c.notes ? ` · ${c.notes}` : ''}</span>
              {c.last_contacted && <span className="text-[10px] text-emerald-600 ml-2">contacted {new Date(c.last_contacted).toLocaleDateString()}</span>}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => generate('outreach', { contact_id: c.id })} disabled={!!generating} data-testid={`ai-contact-pitch-${c.id}`}>Draft pitch</Button>
              <Button size="sm" variant="ghost" className="text-red-400" onClick={async () => { await api(`/contacts/${c.id}`, { method: 'DELETE' }); load(); }}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Investor Scout */}
      <div className="bg-white rounded-xl border p-4" data-testid="ai-investor-scout-card">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h4 className="font-semibold text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-600" /> Investor Scout</h4>
          <Button size="sm" variant="outline" onClick={scoutInvestors} disabled={!!generating} data-testid="ai-investor-scout-btn">
            {generating === 'invScout' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />} Scout investors
          </Button>
        </div>
        <p className="text-xs text-gray-400 mb-3">Finds real Australian angels, seed funds and accelerators that back marketplaces — then writes the pitch with your live traction numbers. Pitches ALWAYS wait for your approval before anything is sent.</p>
        {invProspects && invProspects.length === 0 && (
          <p className="mb-3 text-xs text-red-500 bg-red-50 rounded-lg p-3">Couldn't fetch suggestions — tap "Scout investors" again to retry.</p>
        )}
        {invProspects && invProspects.length > 0 && (
          <div className="mb-3 bg-indigo-50 rounded-lg p-3 space-y-2" data-testid="ai-investor-prospects-list">
            {invProspects.map((p, i) => (
              <div key={i} className="text-xs">
                <span className="font-medium">{p.who}</span>
                {p.firm && <span className="text-indigo-700 ml-1">· {p.firm}</span>}
                {p.stage && <span className="text-gray-400 ml-1">({p.stage})</span>}
                <div className="text-gray-600">{p.why} <span className="text-gray-400">— reach via {p.where_to_find}</span></div>
              </div>
            ))}
            <p className="text-[10px] text-gray-400">Verify them, find their public contact details, then add them below and the Scout drafts the pitch.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-4 gap-2 mb-3">
          <Input placeholder="Investor / fund name" value={newInvestor.name} onChange={e => setNewInvestor({ ...newInvestor, name: e.target.value })} data-testid="ai-investor-name" />
          <Input placeholder="Email (if public)" value={newInvestor.email} onChange={e => setNewInvestor({ ...newInvestor, email: e.target.value })} data-testid="ai-investor-email" />
          <Input placeholder="Notes (e.g. seed, marketplaces)" value={newInvestor.notes} onChange={e => setNewInvestor({ ...newInvestor, notes: e.target.value })} data-testid="ai-investor-notes" />
          <Button size="sm" onClick={addInvestor} data-testid="ai-investor-add">Add investor</Button>
        </div>
        <p className="text-[10px] text-gray-400 mb-1">No email? Add them anyway — the Scout writes a pitch you copy into their website's pitch form.</p>
        {contacts.filter(c => c.type === 'investor').map(c => (
          <div key={c.id} className="flex items-center justify-between py-2 border-t text-sm gap-2 flex-wrap">
            <div>
              <span className="font-medium">{c.name || c.email}</span>
              <span className="text-xs text-gray-400 ml-2">{c.email}{c.notes ? ` · ${c.notes}` : ''}</span>
              {c.last_contacted && <span className="text-[10px] text-emerald-600 ml-2">pitched {new Date(c.last_contacted).toLocaleDateString()}</span>}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => draftInvestorPitch(c.id)} disabled={!!generating} data-testid={`ai-investor-pitch-${c.id}`}>
                {generating === `invPitch-${c.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Draft pitch'}
              </Button>
              <Button size="sm" variant="ghost" className="text-red-400" onClick={async () => { await api(`/contacts/${c.id}`, { method: 'DELETE' }); load(); }}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Report history */}
      {reports.length > 1 && (
        <div className="bg-white rounded-xl border p-4">
          <h4 className="font-semibold text-sm mb-2">Previous CEO reports</h4>
          {reports.slice(1).map(r => (
            <div key={r.id} className="py-2 border-t text-sm">
              <span className="font-medium">{r.headline}</span>
              <span className="text-xs text-gray-400 ml-2">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
